import React, { useEffect, useState } from 'react';

import { Loader } from '@/components/Loader/loader';
import QpsQuestionnaire from '@/components/QpsQuestionnaire/QpsQuestionnaire';
import QpsResult from '@/components/QpsResult/QpsResult';
import QpsUnavailable from '@/components/QpsUnavailable/QpsUnavailable';
import trackQps from '@/utils/functions/tracking/trackQps/trackQps';

import type { BreadcrumbItem } from '@/services/types/Breadcrumb';
import type { QpsEntry, QpsScore } from '@/types/qps';

interface ArbitrageQpsFlowProps {
  entries: QpsEntry[];
  unavailableProductName: string | null;
  // Hands the questionnaires to the QPS context and returns the codes already passed.
  start: () => string[];
  breadcrumbItems: BreadcrumbItem[];
  // Acceptation déclenchée par onAllPassed, en cours côté ArbitrageSummary : affiche le même état de
  // chargement sur le bouton « Continuer » du dernier résultat que sur « Valider » au récapitulatif.
  isAcceptLoading: boolean;
  onExit: () => void;
  onRetryLoad: () => void;
  onAllPassed: () => void;
}

/**
 * Questionnaires between the arbitrage summary and the identity check. Moving to the next
 * questionnaire is a state change; a result is shown in place of its questionnaire, a new attempt
 * remounts it on its first question. The numbering counts every questionnaire of the arbitrage, an
 * already passed one is skipped but counted. Nothing is accepted before `onAllPassed`.
 */
const ArbitrageQpsFlow: React.FC<ArbitrageQpsFlowProps> = ({
  entries,
  unavailableProductName,
  start,
  breadcrumbItems,
  isAcceptLoading,
  onExit,
  onRetryLoad,
  onAllPassed,
}) => {
  // Read once, when the questionnaires are handed to the context: passing one during the flow
  // does not change the queue.
  const [passedCodes, setPassedCodes] = useState<string[] | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [score, setScore] = useState<QpsScore | null>(null);
  const [attempt, setAttempt] = useState(0);
  const isAvailable = unavailableProductName === null;

  useEffect(() => {
    if (isAvailable && passedCodes === null) setPassedCodes(start());
  }, [isAvailable, passedCodes]);

  const firstPendingIndex = passedCodes ? entries.findIndex((entry) => !passedCodes.includes(entry.codeSupport)) : -1;
  // « Réessayer » peut ramener une proposition sans questionnaire à passer : on rend la main au récapitulatif.
  const isNothingToPass = passedCodes !== null && firstPendingIndex === -1;

  useEffect(() => {
    if (isNothingToPass) onExit();
  }, [isNothingToPass]);

  if (!isAvailable) {
    return (
      <QpsUnavailable
        productName={unavailableProductName}
        breadcrumbItems={breadcrumbItems}
        onRetry={onRetryLoad}
        onBackToProposal={onExit}
      />
    );
  }

  if (passedCodes === null || isNothingToPass) return <Loader />;

  const index = selectedIndex ?? firstPendingIndex;
  const nextIndex = entries.findIndex(
    (entry, position) => position > index && !passedCodes.includes(entry.codeSupport)
  );
  const entry = entries[index];
  const nextEntry = nextIndex === -1 ? undefined : entries[nextIndex];
  const rank = entries.length > 1 ? { current: index + 1, total: entries.length } : undefined;

  if (score) {
    return (
      <QpsResult
        isPassed={score.isPassed}
        productName={entry.productName}
        nextProductName={score.isPassed ? nextEntry?.productName : undefined}
        nextQuestionnaireIndex={nextEntry ? nextIndex + 1 : undefined}
        questionnaireIndex={rank?.current}
        questionnaireCount={rank?.total}
        codeSupport={entry.codeSupport}
        attempt={attempt + 1}
        breadcrumbItems={breadcrumbItems}
        isContinueLoading={isAcceptLoading}
        onContinue={() => {
          if (!nextEntry) {
            onAllPassed();
            return;
          }
          setSelectedIndex(nextIndex);
          setScore(null);
          setAttempt(0);
        }}
        onRetry={() => {
          trackQps('nouvel_essai', { codeSupport: entry.codeSupport, attempt: attempt + 2 });
          setAttempt(attempt + 1);
          setScore(null);
        }}
        onBackToProposal={onExit}
      />
    );
  }

  return (
    <QpsQuestionnaire
      key={`${entry.codeSupport}-${attempt}`}
      codeSupport={entry.codeSupport}
      productName={entry.productName}
      productIsin={entry.productIsin}
      introDescription={entry.description}
      avenantReference={entry.avenantReference}
      rank={rank}
      attempt={attempt + 1}
      skipIntroduction={attempt > 0}
      breadcrumbItems={breadcrumbItems}
      onExit={onExit}
      onComplete={(result) => {
        trackQps(result.isPassed ? 'questionnaire_reussi' : 'questionnaire_echoue', {
          codeSupport: entry.codeSupport,
          attempt: attempt + 1,
        });
        setSelectedIndex(index);
        setScore(result);
      }}
    />
  );
};

export default ArbitrageQpsFlow;
